import { Box, Button, IconButton, MenuItem, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ImageIcon from "@mui/icons-material/Image";
import { useState } from "react";
import { getPreSignedUrl } from "../../api/docs";
import { v4 as uuidv4 } from "uuid";

interface Props {
    block: HeroBlock;
    onChange: (content: any) => void;
    onDelete: () => void;
}

const HeroBlockEditor = ({ block, onChange, onDelete }: Props) => {
    const [uploading, setUploading] = useState(false);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const fileName = `page-images/${uuidv4()}-${file.name}`;
            const preSignedUrl = await getPreSignedUrl(fileName, file.type);
            
            await fetch(preSignedUrl, {
                method: "PUT",
                body: file,
                headers: {
                    "Content-Type": file.type,
                },
            });

            const imageUrl = `https://riverhawkoa-docs.s3.amazonaws.com/${fileName}`;
            onChange({
                ...block.content,
                backgroundImage: imageUrl,
            });
        } catch (error) {
            console.error("Failed to upload image:", error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <IconButton size="small" sx={{ cursor: "grab" }}>
                    <DragIndicatorIcon />
                </IconButton>
                <Box flex={1}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                        Hero Block
                    </Typography>
                    <Stack spacing={2}>
                        <TextField
                            label="Title"
                            value={block.content.title || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    title: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Subtitle"
                            value={block.content.subtitle || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    subtitle: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                        />
                        {block.content.backgroundImage ? (
                            <Box>
                                <img
                                    src={block.content.backgroundImage}
                                    alt="Hero background"
                                    style={{ maxWidth: "100%", height: "auto", maxHeight: 200 }}
                                />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    border: "2px dashed #ccc",
                                    borderRadius: 1,
                                    p: 2,
                                    textAlign: "center",
                                }}
                            >
                                <ImageIcon sx={{ fontSize: 32, color: "#ccc" }} />
                            </Box>
                        )}
                        <Button
                            variant="outlined"
                            component="label"
                            disabled={uploading}
                            size="small"
                        >
                            {uploading ? "Uploading..." : "Upload Background Image"}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handleImageUpload}
                            />
                        </Button>
                        <TextField
                            label="Background Overlay (e.g., rgba(0,0,0,0.5))"
                            value={block.content.backgroundOverlay || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    backgroundOverlay: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Height (e.g., 400px, 50vh)"
                            value={block.content.height || "400px"}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    height: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">Text Align:</Typography>
                            <Select
                                value={block.content.textAlign || "center"}
                                onChange={(e) =>
                                    onChange({
                                        ...block.content,
                                        textAlign: e.target.value,
                                    })
                                }
                                size="small"
                            >
                                <MenuItem value="left">Left</MenuItem>
                                <MenuItem value="center">Center</MenuItem>
                                <MenuItem value="right">Right</MenuItem>
                            </Select>
                        </Stack>
                        <TextField
                            label="Button Text (optional)"
                            value={block.content.buttonText || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    buttonText: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Button URL"
                            value={block.content.buttonUrl || ""}
                            onChange={(e) =>
                                onChange({
                                    ...block.content,
                                    buttonUrl: e.target.value,
                                })
                            }
                            fullWidth
                            size="small"
                        />
                    </Stack>
                </Box>
                <IconButton size="small" onClick={onDelete} color="error">
                    <DeleteIcon />
                </IconButton>
            </Stack>
        </Paper>
    );
};

export default HeroBlockEditor;
